import React, { useLayoutEffect, useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useChineseLang } from '../../i18n/chinese'
import { SpeakingModeId } from 'types/writeSpeakTypes'
import ZhWords, { ClickWordHandler } from './ZhText'
import { segmentText } from './logic'

// name and hint are translation keys.
const MODES: Array<{ id: SpeakingModeId; name: string; hint: string; icon: React.ReactNode }> = [
  {
    id: 'read_writing',
    name: 'writeSpeak:modes.name.read_writing',
    hint: 'writeSpeak:modes.hint.read_writing',
    icon: (
      <>
        <path d='M5 4h10l4 4v12H5z' />
        <path d='M9 11h6M9 15h6' />
      </>
    ),
  },
  {
    id: 'free_talk',
    name: 'writeSpeak:modes.name.free_talk',
    hint: 'writeSpeak:modes.hint.free_talk',
    icon: (
      <>
        <path d='M4 5h16v11H9l-5 4z' />
        <path d='M8 10h.01M12 10h.01M16 10h.01' />
      </>
    ),
  },
  {
    id: 'read_passage',
    name: 'writeSpeak:modes.name.read_passage',
    hint: 'writeSpeak:modes.hint.read_passage',
    icon: (
      <>
        <path d='M4 5c3-1 6-1 8 1 2-2 5-2 8-1v14c-3-1-6-1-8 1-2-2-5-2-8-1z' />
        <path d='M12 6v14' />
      </>
    ),
  },
]

interface ModePickerProps {
  mode: SpeakingModeId
  onChange: (mode: SpeakingModeId) => void
  hasText: boolean
  /** A take of "Read my writing" exists, so the mode stays selectable even with the answer cleared. */
  holdsWritingTake: boolean
  disabled?: boolean
}

/** Three ways to speak. Switching is instant: the selected card and its script swap with no motion. */
export const ModePicker: React.FC<ModePickerProps> = ({ mode, onChange, hasText, holdsWritingTake, disabled }) => {
  const { t } = useTranslation('writeSpeak')
  const refs = useRef<Record<string, HTMLButtonElement | null>>({})
  const isDisabled = (id: SpeakingModeId) => !!disabled || (id === 'read_writing' && !hasText && !holdsWritingTake)

  const onKeyDown = (e: React.KeyboardEvent, id: SpeakingModeId) => {
    if (['ArrowRight', 'ArrowLeft', 'ArrowDown', 'ArrowUp'].indexOf(e.key) === -1) return
    e.preventDefault()
    const enabled = MODES.filter((m) => !isDisabled(m.id))
    const idx = enabled.findIndex((m) => m.id === id)
    const step = e.key === 'ArrowRight' || e.key === 'ArrowDown' ? 1 : -1
    const next = enabled[(idx + step + enabled.length) % enabled.length]
    if (!next) return
    onChange(next.id)
    refs.current[next.id]?.focus()
  }

  return (
    <div className='ws-modes' role='radiogroup' aria-label={t('modes.pickerLabel')}>
      {MODES.map((m) => (
        <button
          key={m.id}
          ref={(el) => {
            refs.current[m.id] = el
          }}
          type='button'
          className='ws-mode'
          role='radio'
          aria-checked={mode === m.id}
          tabIndex={mode === m.id ? 0 : -1}
          disabled={isDisabled(m.id)}
          onClick={() => onChange(m.id)}
          onKeyDown={(e) => onKeyDown(e, m.id)}
        >
          <svg viewBox='0 0 24 24' aria-hidden='true'>
            {m.icon}
          </svg>
          <b>{t(m.name)}</b>
          <span>{m.id === 'read_writing' && !hasText ? t('modes.writeFirst') : t(m.hint)}</span>
        </button>
      ))}
    </div>
  )
}

interface ScriptProps {
  mode: SpeakingModeId
  story: any
  activeIndex: string
  onClickWord: ClickWordHandler
  known: Map<string, { pinyin?: string; english?: string }>
  // read_writing
  answerText: string
  /** The text they actually read, once a "Read my writing" take exists. */
  frozenText: string | null
  onReRecord: () => void
  // free_talk
  questionContent: any[]
  wordsMirror?: React.ReactNode
  sayHint?: React.ReactNode
  // read_passage
  paragraphs: any[][]
  paragraphIndex: number
  onNextParagraph: () => void
}

/** "Your answer" / "What you read" with the phone clamp and Show all. */
const WritingScript: React.FC<
  Pick<ScriptProps, 'answerText' | 'frozenText' | 'onReRecord' | 'story' | 'activeIndex' | 'onClickWord' | 'known'>
> = ({ answerText, frozenText, onReRecord, story, activeIndex, onClickWord, known }) => {
  const { t } = useTranslation('writeSpeak')
  const zhLang = useChineseLang()
  const held = frozenText !== null
  const shown = held ? (frozenText as string) : answerText
  const stale = held && frozenText !== answerText
  const textRef = useRef<HTMLDivElement>(null)
  const [expanded, setExpanded] = useState(false)
  const [overflows, setOverflows] = useState(false)

  useLayoutEffect(() => {
    const el = textRef.current
    if (!el) return
    const measure = () => setOverflows(!expanded && el.scrollHeight > el.clientHeight + 2)
    measure()
    if (typeof ResizeObserver === 'undefined') return
    const ro = new ResizeObserver(measure)
    ro.observe(el)
    return () => ro.disconnect()
  }, [shown, expanded])

  const lines = shown.split('\n')
  return (
    <div className='ws-script'>
      <div className='ws-script-row'>
        <span className='ws-tiny'>{held ? t('script.whatYouRead') : t('script.yourAnswer')}</span>
      </div>
      <div className={`ws-zhbig${expanded ? '' : ' clamp'}`} lang={zhLang} ref={textRef}>
        {lines.map((line, i) => (
          <div key={i}>
            <ZhWords
              words={segmentText(line, known)}
              iterator={`WS-SCRIPT-${i}-`}
              story={story}
              activeIndex={activeIndex}
              onClickWord={onClickWord}
            />
          </div>
        ))}
      </div>
      {overflows && (
        <button type='button' className='ws-showall' onClick={() => setExpanded(true)}>
          {t('script.showAll')}
        </button>
      )}
      {stale && (
        <div className='ws-stale'>
          {t('script.stale')}{' '}
          <button type='button' className='ws-inline-link' onClick={onReRecord}>
            {t('script.recordAgain')}
          </button>
        </div>
      )}
    </div>
  )
}

export const Script: React.FC<ScriptProps> = (props) => {
  const { mode, story, activeIndex, onClickWord } = props
  const { t } = useTranslation('writeSpeak')
  const zhLang = useChineseLang()

  if (mode === 'read_writing') return <WritingScript {...props} />

  if (mode === 'free_talk') {
    return (
      <div className='ws-script'>
        <div className='ws-script-row'>
          <span className='ws-tiny'>{t('script.freeTalkPrompt')}</span>
          <span className='ws-tiny'>{t('script.freeTalkAim')}</span>
        </div>
        <div className='ws-zhbig' lang={zhLang}>
          <ZhWords
            words={props.questionContent}
            iterator='WS-FREETALK-'
            story={story}
            activeIndex={activeIndex}
            onClickWord={onClickWord}
          />
        </div>
        {props.wordsMirror && <div className='ws-ft-mirror'>{props.wordsMirror}</div>}
        {props.sayHint && <p className='ws-tap-hint'>{props.sayHint}</p>}
      </div>
    )
  }

  const total = props.paragraphs.length
  const paragraph = props.paragraphs[props.paragraphIndex] || []
  return (
    <div className='ws-script'>
      <div className='ws-script-row'>
        <span className='ws-tiny'>{total ? t('script.paragraphOf', { index: props.paragraphIndex + 1, total }) : ''}</span>
        {total > 1 && (
          <button type='button' onClick={props.onNextParagraph}>
            {t('script.anotherParagraph')}
          </button>
        )}
      </div>
      <div className='ws-zhbig' lang={zhLang}>
        <ZhWords
          words={paragraph}
          iterator={`WS-PASSAGE-${props.paragraphIndex}-`}
          story={story}
          activeIndex={activeIndex}
          onClickWord={onClickWord}
          showPinYin={true}
          pinyinSize='11px'
        />
      </div>
    </div>
  )
}
